/* hero-carousel.js — PRD §5.1
   A background slideshow, not a carousel: images cross-fade, the headline
   and CTAs never move. Only the eyebrow caption changes per slide.
   Vanilla, no dependencies. */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function HeroCarousel(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-ag-slide]'));
    this.ticks = Array.prototype.slice.call(root.querySelectorAll('[data-ag-tick]'));
    this.eyebrow = root.querySelector('[data-ag-eyebrow]');
    this.interval = parseInt(root.getAttribute('data-ag-interval'), 10) || 6000;
    this.index = 0;
    this.timer = null;
    this.paused = false;
    this.captionTimer = null;
    this.coverTimer = null;
    this.coverIndex = 0;         // the slide currently held at full opacity
    this.ready = false;          // the first show() must not animate the caption

    if (this.slides.length < 2) {
      this.show(0);
      this.ready = true;
      return;
    }

    this.bind();
    this.show(0);
    this.ready = true;
    if (!REDUCED.matches) this.start();
  }

  HeroCarousel.prototype.bind = function () {
    var self = this;

    // Pause while a pointer or finger is on the hero (§5.1)
    this.root.addEventListener('mouseenter', function () { self.hold(true); });
    this.root.addEventListener('mouseleave', function () { self.hold(false); });
    this.root.addEventListener('touchstart', function () { self.hold(true); }, { passive: true });
    this.root.addEventListener('touchend', function () { self.hold(false); }, { passive: true });

    // Keyboard operable (§5.1)
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.manual(self.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.manual(self.index - 1); }
    });

    // Ticks are real controls, not decoration
    this.ticks.forEach(function (tick, i) {
      tick.addEventListener('click', function () { self.manual(i); });
    });

    REDUCED.addEventListener('change', function (e) {
      if (e.matches) self.stop();
      else if (!self.paused) self.start();
    });
  };

  HeroCarousel.prototype.show = function (i) {
    var n = this.slides.length;
    this.index = (i + n) % n;

    // Exactly one slide is held at full opacity beneath the one fading in, so
    // stacked coverage never drops below 1 and the ink surface never shows
    // through. Holding it here rather than in CSS is what makes an
    // interrupted cross-fade safe: a slide caught mid-fade is simply dropped,
    // because the cover underneath is already opaque.
    var cover = this.coverIndex;

    this.slides.forEach(function (slide, j) {
      var active = j === this.index;
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-cover', j === cover && !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');

      // Restart Ken Burns on the newly active slide
      if (active && !REDUCED.matches) {
        var img = slide.querySelector('.ag-hero__img');
        if (img) {
          img.style.animation = 'none';
          void img.offsetWidth;          // force reflow
          img.style.animation = '';
        }
      }
    }, this);

    this.ticks.forEach(function (tick, j) {
      tick.classList.toggle('is-active', j === this.index);
      tick.setAttribute('aria-current', j === this.index ? 'true' : 'false');
    }, this);

    if (this.eyebrow) {
      var caption = this.slides[this.index].getAttribute('data-ag-caption') || '';
      var el = this.eyebrow;

      // Swap the caption while it is invisible. The image needs 900ms to
      // cross-fade; replacing the text on the first frame left the caption
      // naming a slide that was not on screen yet for most of that — a
      // "Beluga caviar" line sitting over the wagyu frame. The fade is
      // opacity only, so nothing moves (§4.7).
      if (!this.ready || REDUCED.matches) {
        el.style.opacity = '';
        el.textContent = caption;
      } else {
        clearTimeout(this.captionTimer);
        el.style.opacity = '0';
        this.captionTimer = setTimeout(function () {
          el.textContent = caption;
          el.style.opacity = '';   // back to the authored .92, not a hard 1
        }, 420);
      }
    }

    // §11 wants hero_cta reported with the slide that was showing. The CTAs
    // sit outside the slides, so the index lives on the hero element and the
    // analytics listener reads it from there.
    this.root.setAttribute('data-ag-slide-index', String(this.index + 1));

    // Once the incoming slide is opaque in its own right it becomes the cover
    // and the previous one can be released. Restarting this timer on every
    // show() is what keeps an interrupted transition correct.
    clearTimeout(this.coverTimer);
    var self = this;
    this.coverTimer = setTimeout(function () {
      self.coverIndex = self.index;
      self.slides.forEach(function (slide, j) {
        slide.classList.toggle('is-cover', false);
      });
    }, 900);
  };

  HeroCarousel.prototype.next = function () { this.show(this.index + 1); };

  // Using a control stops the rotation for the rest of the visit. With the
  // pause glyph gone this is the WCAG 2.2.2 mechanism: a keyboard-reachable
  // way to halt content that would otherwise advance every 6s on its own.
  HeroCarousel.prototype.manual = function (i) {
    this.paused = true;
    this.stop();
    this.show(i);
  };

  HeroCarousel.prototype.start = function () {
    var self = this;
    this.stop();
    this.timer = window.setInterval(function () { self.next(); }, this.interval);
  };

  HeroCarousel.prototype.stop = function () {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
  };

  // Transient hold (hover/touch) — does not change the button's state
  HeroCarousel.prototype.hold = function (on) {
    if (this.paused || REDUCED.matches) return;
    on ? this.stop() : this.start();
  };

  function init() {
    document.querySelectorAll('[data-ag-hero]').forEach(function (el) {
      if (!el.agHero) el.agHero = new HeroCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', init);
})();
